import { memo } from "react";
import { ChevronRight, FileCode2 } from "lucide-react";

import type { TreeNode } from "@acme/ui/tree";
import type { AstGrepMatchSchemaType } from "@acme/validators";
import { cn } from "@acme/ui";
import { Badge } from "@acme/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@acme/ui/tooltip";
import { Tree } from "@acme/ui/tree";

import { getExtensionIcon } from "../../_utils/languages";

interface MatchFileData extends Record<string, unknown> {
  filePath: string;
  fileSource: {
    file: string;
    source?: string;
    matches: AstGrepMatchSchemaType[];
  };
  fileName: string;
  dirName: string;
}

interface SearchResultItemProps {
  item: TreeNode;
  index: number;
  isFocused: boolean;
  onItemClick: (index: number, item: TreeNode) => void;
}

const SearchResultItem = memo(
  ({ item, index, isFocused, onItemClick }: SearchResultItemProps) => {
    const icon = getExtensionIcon(item.path).icon;

    if (item.type === "group") {
      const data = item.data as MatchFileData | undefined;
      const matchCount = data?.fileSource.matches.length ?? 0;
      return (
        <Tooltip delayDuration={1000}>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "hover:bg-muted/50 focus:bg-muted/50 active:bg-muted/50 flex cursor-pointer items-center py-1 pr-2 pl-[var(--tree-indent)] select-none focus:outline-1 focus:outline-blue-500",
                isFocused && "bg-muted/50 outline-1 outline-blue-500",
              )}
              role="treeitem"
              aria-level={item.level + 1}
              aria-label={`${item.name} file, ${matchCount} matches`}
              tabIndex={-1}
            >
              <button
                type="button"
                className="inline-flex flex-1 items-center gap-1.5 overflow-hidden"
                onClick={() => onItemClick(index, item)}
                tabIndex={-1}
              >
                <ChevronRight
                  className={cn(
                    "text-muted-foreground group-hover:text-foreground group-focus:text-foreground group-active:text-foreground size-4 transition-transform",
                    item.isExpanded && "rotate-90",
                    isFocused && "text-foreground",
                  )}
                  aria-hidden="true"
                />
                <div className="size-4" aria-hidden="true">
                  {icon}
                </div>

                <div className="flex flex-1 items-center justify-between gap-1.5 overflow-hidden">
                  <div className="flex flex-col items-start overflow-hidden">
                    <div className="flex items-center gap-1">
                      <span
                        className={cn(
                          "text-foreground truncate text-xs font-medium",
                          isFocused && "text-foreground",
                        )}
                      >
                        {item.name}
                      </span>
                      <span
                        className={cn(
                          "text-muted-foreground truncate text-xs",
                          isFocused && "text-muted-foreground",
                        )}
                      >
                        {data?.dirName}
                      </span>
                    </div>
                  </div>
                  <div className="ml-0.5 flex items-center gap-1">
                    {matchCount > 0 && (
                      <Badge className="bg-primary/10 text-primary hover:bg-primary/20 inline-flex size-4 items-center justify-center rounded-full p-0 font-mono text-[11px] tabular-nums">
                        {matchCount}
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-md">
            <div className="space-y-1">
              <p className="font-mono text-xs">
                {item.path}/{item.name}
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    if (item.type === "preview") {
      const match = item.data as AstGrepMatchSchemaType | undefined;
      return (
        <div
          className={cn(
            "hover:bg-muted/30 focus:bg-muted/30 active:bg-muted/30 flex h-6 cursor-pointer items-center py-1 select-none focus:outline-1 focus:outline-blue-500",
            isFocused && "bg-muted/30 outline-1 outline-blue-500",
          )}
          role="treeitem"
          aria-level={item.level + 1}
          aria-label={`Match: ${match?.text}`}
          tabIndex={-1}
        >
          <button
            type="button"
            className="inline-flex flex-1 items-center gap-1.5 overflow-hidden"
            onClick={() => onItemClick(index, item)}
            tabIndex={-1}
          >
            <div className="size-4" aria-hidden="true" />
            <div className="flex flex-1 items-center justify-start overflow-hidden text-left">
              <div className="flex flex-col items-start overflow-hidden">
                <pre className="text-muted-foreground line-clamp-1 font-mono text-xs whitespace-pre-wrap">
                  {match?.text}
                </pre>
              </div>
            </div>
          </button>
        </div>
      );
    }

    return null;
  },
);

const EmptyState = () => (
  <div className="text-muted-foreground flex flex-col items-center justify-center py-10 text-center">
    <FileCode2 className="mb-2 size-12" />
    <p className="text-sm">No files to display</p>
  </div>
);
interface SearchResultProps {
  data?: {
    type: "group";
    name: string;
    path: string;
    data: MatchFileData;
    children: {
      type: "preview";
      name: string;
      path: string;
      data: AstGrepMatchSchemaType;
    }[];
  }[];
  onItemClick?: (item: TreeNode) => void;
}

export function SearchResult({ data, onItemClick }: SearchResultProps = {}) {
  return (
    <Tree
      data={data ?? []}
      itemHeight={24}
      onItemClick={onItemClick}
      renderItem={({ item, index, isFocused, onItemClick }) => (
        <SearchResultItem
          item={item}
          index={index}
          isFocused={isFocused}
          onItemClick={onItemClick}
        />
      )}
      emptyState={<EmptyState />}
      isExpandAll
    />
  );
}
